import { Link, usePage } from "@inertiajs/react"
import { Bell } from "lucide-react"
import { TimeAgo } from "@/components/TimeAgo"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu"
import { read_all_notifications_path, read_notification_path } from "@/lib/routes"
import { TOP_BAR_CELL } from "./topBarCell"

interface NotificationItem {
  id: string
  message: string
  link?: string | null
  category?: string | null
  read: boolean
  created_at: string
}

// Inertia serves Back/Forward from its client-side history cache with no server
// round-trip, so the bell's shared props revert to whatever was cached when that
// page was first visited — a just-read notification looks unread again. Rather than
// re-fetch after the stale restore (which visibly flashes old → correct), we remember
// which notifications the user read in this session in a module-scope set. It survives
// Inertia's SPA remounts (same JS context), so when AppLayout remounts on Back it can
// render the bell already-correct on the first paint. Cleared only by a full reload,
// which also re-fetches fresh server props, so the two never disagree.
const locallyReadIds = new Set<string>()

export default function NotificationBell() {
  const { props } = usePage<{ unread_notifications_count?: number; notifications?: NotificationItem[] }>()
  const unreadCount = props.unread_notifications_count || 0
  const notifications = props.notifications || []
  // Apply this session's optimistic reads so a stale history-cache restore (Back)
  // shows the bell already-corrected instead of reverting to unread.
  const displayNotifications = notifications.map((n) => (locallyReadIds.has(n.id) ? { ...n, read: true } : n))
  const locallyReadUnread = notifications.filter((n) => !n.read && locallyReadIds.has(n.id)).length
  const displayUnread = Math.max(0, unreadCount - locallyReadUnread)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Notifications" className={TOP_BAR_CELL}>
          <Bell size={16} color="var(--muted-foreground)" strokeWidth={2} />
          {displayUnread > 0 && (
            <span style={{ position: "absolute", top: 6, right: 6, minWidth: 18, height: 18, borderRadius: 9, background: "var(--destructive)", color: "var(--background)", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center", padding: "0 5px", boxShadow: "0 0 0 2px var(--background)" }}>
              {displayUnread > 99 ? "99+" : displayUnread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0 text-sm font-semibold text-foreground">
            Notifications
          </DropdownMenuLabel>
          {displayUnread > 0 && (
            <Link
              href={read_all_notifications_path()}
              method="patch"
              as="button"
              preserveScroll
              onClick={() => notifications.forEach((n) => locallyReadIds.add(n.id))}
              className="text-xs text-link hover:underline"
            >
              Mark all read
            </Link>
          )}
        </div>
        <DropdownMenuSeparator />
        {displayNotifications.length === 0 ? (
          <div className="flex flex-col items-center gap-1 px-4 py-8 text-center">
            <Bell size={20} color="var(--muted-foreground)" strokeWidth={1.5} />
            <p className="text-sm font-medium text-foreground">No notifications</p>
            <p className="text-xs text-muted-foreground">You're all caught up.</p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto py-1">
            {displayNotifications.map((n) => (
              <Link
                key={n.id}
                href={read_notification_path(n.id)}
                method="patch"
                as="button"
                onClick={() => locallyReadIds.add(n.id)}
                className={`flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted/50 ${n.read ? "" : "bg-muted/30"}`}
              >
                <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-primary"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-foreground">{n.message}</span>
                  <TimeAgo at={n.created_at} className="mt-0.5 block text-xs text-muted-foreground" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
